import {Banner} from '@wandb/components';
import React from 'react';
import {useLocation} from 'react-router-dom';
import {Button, Modal} from 'semantic-ui-react';

import {useLicenseExpirationUiQuery} from '../generated/graphql';
import {
  createDismissibleBanner,
  DEFAULT_BANNER_SHOW_AFTER,
} from '../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {LOGIN_PATH} from '../routes/auth/authPaths';
import {ONBOARDING_FLOW_PATH} from '../routes/onboarding/onboardingPaths';

const LOCAL_LICENSE_EXPIRATION_NUDGE_BANNER_ID =
  'local-license-expiration-nudge';

const LocalLicenseNudgeBarBanner = createDismissibleBanner<{
  licenseExpirationUI: {
    bannerDaysRemaining: number;
    bannerTotalDays: number;
    renewUrl?: string | null;
  };
}>({
  id: () => LOCAL_LICENSE_EXPIRATION_NUDGE_BANNER_ID,
  showAfter: DEFAULT_BANNER_SHOW_AFTER,
  Component: ({onDismiss, licenseExpirationUI}) => {
    return (
      <Banner
        variant="global"
        isDismissible
        isVisible
        onDismiss={async () => {
          window.analytics?.track('License Expiration Nudge Dismissed');
          await onDismiss();
        }}
        aria-label="License expired for W&B Local">
        <Banner.Body>
          You need to renew your license immediately. In{' '}
          {licenseExpirationUI.bannerDaysRemaining}{' '}
          {licenseExpirationUI.bannerDaysRemaining === 1 ? 'day' : 'days'}, the
          UI will shut down.{' '}
          {licenseExpirationUI.renewUrl == null
            ? 'Ask your admin to get a new license.'
            : ''}
        </Banner.Body>
        {licenseExpirationUI.renewUrl != null ? (
          <Banner.Action
            href={licenseExpirationUI.renewUrl}
            onPress={() => {
              window.analytics?.track('Renew License Clicked');
            }}>
            {licenseExpirationUI.renewUrl.endsWith('local-contact')
              ? 'Contact sales'
              : 'Manage license'}
          </Banner.Action>
        ) : null}
      </Banner>
    );
  },
});

const LocalLicenseNudgeBar: React.FC = () => {
  const licenseExpirationUIQuery = useLicenseExpirationUiQuery();
  const licenseExpirationUI =
    licenseExpirationUIQuery.data?.serverInfo?.licenseExpirationUI ?? null;

  const location = useLocation();

  // either we're not currently expiring, or we're on an exempt page
  // (like the landing page or login UI)
  if (
    licenseExpirationUI == null ||
    ['/', LOGIN_PATH, ONBOARDING_FLOW_PATH].includes(location.pathname)
  ) {
    return null;
  }

  return licenseExpirationUI.bannerDaysRemaining > 0 ? (
    <LocalLicenseNudgeBarBanner licenseExpirationUI={licenseExpirationUI} />
  ) : (
    <Modal className="license-expired-block" open>
      <Modal.Header>License Expired</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <p>
            You need to renew your license immediately. You can continue logging
            data, but since the {licenseExpirationUI.bannerTotalDays}
            -day grace period has elapsed, you won't be able to use the UI until
            you renew your license.{' '}
            {!licenseExpirationUI.renewUrl &&
              'Ask your admin to get a new license.'}
          </p>
        </Modal.Description>
      </Modal.Content>
      {licenseExpirationUI.renewUrl && (
        <Modal.Actions>
          <Button as="a" primary href={licenseExpirationUI.renewUrl}>
            {licenseExpirationUI.renewUrl.endsWith('local-contact')
              ? 'Contact sales'
              : 'Manage license'}
          </Button>
        </Modal.Actions>
      )}
    </Modal>
  );
};

export default LocalLicenseNudgeBar;
